
import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ChartData {
  totalReferrals: number;
  soldReferrals: number;
  totalCommission: number;
  conversionRate: number;
  monthlyStats: Record<string, { total: number; sold: number }>;
}

interface ChartComponentProps {
  data: ChartData;
}

const ChartComponent: React.FC<ChartComponentProps> = ({ data }) => {
  // Convert monthly stats to array for chart
  const monthlyData = Object.entries(data.monthlyStats || {}).map(([month, stats]) => ({
    name: month,
    Referrals: stats.total,
    Sales: stats.sold,
  })).slice(-6); // Last 6 months
  
  // Prepare data for conversion rate pie chart
  const conversionData = [
    { name: 'Converted', value: data.soldReferrals },
    { name: 'Pending', value: data.totalReferrals - data.soldReferrals }
  ];
  
  const COLORS = ['#F97316', '#3F3F46'];
  
  const formatCurrency = (value: number) => {
    return `$${value.toFixed(2)}`;
  };
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 animate-fadeIn">
      {/* Stats Cards */}
      <Card className="glass-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">Total Referrals</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold text-gradient">{data.totalReferrals}</div>
          <p className="text-sm text-muted-foreground mt-2">
            Total people you've referred
          </p>
        </CardContent>
      </Card>
      
      <Card className="glass-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">Conversion Rate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold text-gradient">
            {data.conversionRate.toFixed(1)}%
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {data.soldReferrals} of {data.totalReferrals} referrals converted
          </p>
        </CardContent>
      </Card>
      
      <Card className="glass-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">Total Earnings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold text-gradient">
            {formatCurrency(data.totalCommission)}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Total commission earned
          </p>
        </CardContent>
      </Card>
      
      {/* Charts */}
      <Card className="glass-panel lg:col-span-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">Monthly Performance</CardTitle>
          <CardDescription>Referrals and sales over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monthlyData}
                margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.2} />
                <XAxis dataKey="name" stroke="#888" />
                <YAxis stroke="#888" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(30, 30, 36, 0.9)', 
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Bar dataKey="Referrals" fill="#3F3F46" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Sales" fill="#F97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      
      <Card className="glass-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">Conversion Rate</CardTitle>
          <CardDescription>Sold vs pending referrals</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={conversionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {conversionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(30, 30, 36, 0.9)', 
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChartComponent;
